class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
    
    // Add input event listener to prevent 0 values in real-time
    this.addEventListener('input', (event) => {
      if (event.target.classList.contains('quantity__input')) {
        this.onQuantityInput(event);
      }
    });
    
    // Add blur event listener to auto-correct invalid values
    this.addEventListener('blur', (event) => {
      if (event.target.classList.contains('quantity__input')) {
        this.onQuantityBlur(event);
      }
    }, true);
  }
  
  onQuantityInput(event) {
    const input = event.target;
    const value = input.value;
    
    // Remove leading zeros (except for empty or single "0")
    if (value.length > 1 && value.startsWith('0')) {
      input.value = parseInt(value, 10) || '';
    }
  }
  
  onQuantityBlur(event) {
    const input = event.target;
    const value = parseInt(input.value, 10);
    const min = parseInt(input.dataset.min) || 1;
    
    // If value is 0, empty, or less than minimum, reset to minimum
    if (isNaN(value) || value < min) {
      // If value is 0, just reset to minimum without triggering update
      // (as 0 means user tried to remove but typed it instead of using button)
      if (value === 0) {
        input.value = input.getAttribute('value');
        input.setCustomValidity('');
      } else if (isNaN(value) || value < min) {
        input.value = input.getAttribute('value');
      }
    }
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Skip if the event came from this component itself (prevent infinite loop)
      if (event.source === this.tagName.toLowerCase()) {
        return;
      }
      // Skip if the event came from product-form - renderContents already handles the update
      if (event.source === 'product-form') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`) || this.querySelector(`#Drawer-quantity-${id}`);
    if (input) {
      input.value = input.getAttribute('value');
    }
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    const min = parseInt(event.target.dataset.min) || 1;
    let message = '';

    // If value is 0 or less than minimum, auto-correct it instead of showing error
    if (isNaN(inputValue) || inputValue <= 0 || inputValue < min) {
      // Reset to original value without showing error
      event.target.value = event.target.getAttribute('value');
      event.target.setCustomValidity('');
      return;
    } else if (event.target.max && inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          // Re-initialize "You may also like" Swiper after content update
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      // Fetch both cart items and cart footer sections
      const mainCartFooter = document.getElementById('main-cart-footer');
      const sectionsToFetch = ['main-cart-items'];
      
      if (mainCartFooter) {
        sectionsToFetch.push(mainCartFooter.dataset.id);
      }
      
      return fetch(`${routes.cart_url}?sections=${sectionsToFetch.join(',')}`)
        .then((response) => response.json())
        .then((sections) => {
          // Update cart items
          const cartItemsHtml = new DOMParser().parseFromString(sections['main-cart-items'], 'text/html');
          const sourceQty = cartItemsHtml.querySelector('cart-items');
          if (sourceQty) {
            this.innerHTML = sourceQty.innerHTML;
          }
          
          // Update cart footer if it exists
          if (mainCartFooter && sections[mainCartFooter.dataset.id]) {
            const footerHtml = new DOMParser().parseFromString(sections[mainCartFooter.dataset.id], 'text/html');
            const sourceFooter = footerHtml.querySelector('.js-contents');
            const targetFooter = mainCartFooter.querySelector('.js-contents');
            if (sourceFooter && targetFooter) {
              targetFooter.innerHTML = sourceFooter.innerHTML;
            }
          }
          
          // Update cart icon bubble
          return fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
            .then((res) => res.text())
            .then((html) => {
              const iconBubble = document.getElementById('cart-icon-bubble');
              if (iconBubble) {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newIconBubble = doc.querySelector('.shopify-section');
                if (newIconBubble) {
                  iconBubble.innerHTML = newIconBubble.innerHTML;
                }
              }
            });
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);
    
    // Store the requested quantity before making the API call
    const requestedQuantity = quantity;
    const previousItemCount = document.querySelectorAll('.cart-item').length;

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          // Get quantity element before DOM update for error handling
          let quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);

          if (parsedState.errors) {
            if (quantityElement) {
              quantityElement.value = quantityElement.getAttribute('value');
            }
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const sectionElement = document.getElementById(section.id);
            if (!sectionElement) return;
            const elementToReplace =
              sectionElement.querySelector(section.selector) || sectionElement;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          
          // Re-query the quantity element after DOM update (since it was replaced)
          quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          
          // Check if quantity was limited due to availability
          if (previousItemCount === parsedState.items.length && updatedValue !== undefined && updatedValue !== requestedQuantity) {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          } else if (previousItemCount === parsedState.items.length && typeof updatedValue === 'undefined' && requestedQuantity > 0) {
            message = window.cartStrings.error;
          }
          
          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: this.tagName.toLowerCase(), cartData: parsedState, variantId: variantId });
        
        // Re-initialize "You may also like" Swiper after cart drawer content update
        if (this.tagName === 'CART-DRAWER-ITEMS') {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.textContent = message;
    }

    // Re-query the status element in case DOM was replaced
    const lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    if (lineItemStatusElement) lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      if (cartStatus) cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    const lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    if (lineItemStatusElement) lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
